import * as THREE from 'three';
import type { SkillEffect } from './skill-effects.js';
import { effectMaterial, shadeEffect } from './effect-surfaces.js';

const DURATION = 1, REACH = 4, FEAR = 0xc5a4ff;
/** Travelling rings across the shell: the refraction wobbles as the wave passes. */
const HOWL_RIPPLE = 'sin(vWarp.y * 16.0 - uTime * 22.0) * cos(atan(vWarp.z, vWarp.x) * 7.0 + uTime * 5.0)';

/** The frame drawn so far, copied once per render for every distortion shell in it. */
const behind = new WeakMap<THREE.WebGLRenderer, { texture: THREE.FramebufferTexture; render: number }>();
const drawn = new THREE.Vector2();

/** Copies what the renderer has drawn so far, unless this render already did. */
function copyBehind(renderer: THREE.WebGLRenderer, uniform: { value: THREE.Texture | null }) {
  const target = renderer.getRenderTarget();
  // A multisampled target cannot be copied from; the shell then bends the last copy.
  if (target && target.samples > 0) return;
  if (target) drawn.set(target.width, target.height); else renderer.getDrawingBufferSize(drawn);
  if (drawn.x < 1 || drawn.y < 1) return;
  // A copy must match the buffer's format: the picture filters draw the world into a half-float target.
  const type = target?.texture.type ?? THREE.UnsignedByteType;
  let copy = behind.get(renderer);
  if (copy && (copy.texture.image.width !== drawn.x || copy.texture.image.height !== drawn.y || copy.texture.type !== type)) { copy.texture.dispose(); copy = undefined; }
  if (!copy) {
    const texture = new THREE.FramebufferTexture(drawn.x, drawn.y); texture.minFilter = texture.magFilter = THREE.LinearFilter; texture.type = type;
    copy = { texture, render: -1 }; behind.set(renderer, copy);
  }
  uniform.value = copy.texture;
  if (copy.render === renderer.info.render.frame) return;
  copy.render = renderer.info.render.frame;
  renderer.copyFramebufferToTexture(copy.texture);
}

/**
 * A shell that bends whatever lies behind it: it refracts a copy of the frame drawn so far, as three's transmission would,
 * without the transmission pass (a second draw of every opaque object while it shows, and a recompile of each of their
 * shaders the first time). `ripple` is a GLSL float over the shell's local position `vWarp` and `uTime`, turned into a
 * screen-space normal tilt scaled by `uRipple`; `uIor` and `uThickness` set how far the view behind is bent.
 */
export function distortionShell(name: string, ripple = HOWL_RIPPLE, tint = 0xe6dcff, tilt = 3.5) {
  // Beer's law over the refracted path, as a transmissive surface with this attenuation colour at 8 m would tint it.
  const absorb = new THREE.Vector3(...new THREE.Color(tint).toArray().map(channel => -Math.log(Math.max(channel, 1e-4)) / 8));
  const uniforms = { uTime: { value: 0 }, uRipple: { value: 0 }, uIor: { value: 1 }, uThickness: { value: 0 },
    uBehind: { value: null as THREE.Texture | null }, uAbsorb: { value: absorb } };
  const material = new THREE.ShaderMaterial({ name, uniforms, depthWrite: false, toneMapped: false,
    vertexShader: `
      varying vec3 vWarp; varying vec3 vWorld; varying vec3 vNormal;
      void main() {
        vWarp = position;
        vec4 world = modelMatrix * vec4(position, 1.0);
        vWorld = world.xyz; vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * viewMatrix * world;
      }`,
    fragmentShader: `
      uniform mat4 modelMatrix; uniform mat4 projectionMatrix;
      uniform float uTime; uniform float uRipple; uniform float uIor; uniform float uThickness;
      uniform sampler2D uBehind; uniform vec3 uAbsorb;
      varying vec3 vWarp; varying vec3 vWorld; varying vec3 vNormal;
      void main() {
        float warpField = ${ripple};
        vec3 normal = normalize(normalize(vNormal) + vec3(dFdx(warpField), dFdy(warpField), 0.0) * ${tilt.toFixed(2)} * uRipple);
        vec3 n = normalize((vec4(normal, 0.0) * viewMatrix).xyz), v = normalize(cameraPosition - vWorld);
        vec3 scale = vec3(length(modelMatrix[0].xyz), length(modelMatrix[1].xyz), length(modelMatrix[2].xyz));
        vec3 ray = normalize(refract(-v, n, 1.0 / uIor)) * uThickness * scale;
        vec4 exit = projectionMatrix * viewMatrix * vec4(vWorld + ray, 1.0);
        vec3 seen = texture2D(uBehind, exit.xy / exit.w * 0.5 + 0.5).rgb;
        gl_FragColor = vec4(seen * exp(-uAbsorb * length(ray)), 1.0);
      }` });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 24), material);
  mesh.name = `${name}-shell`; mesh.renderOrder = 2;
  // Drawn after the opaque world, so the copy holds what lies behind it. Outline and depth passes draw it with other materials.
  mesh.onBeforeRender = (renderer, _scene, _camera, _geometry, drawnWith) => { if (drawnWith === material) copyBehind(renderer, uniforms.uBehind); };
  return { mesh, material, uniforms };
}

/** Fearsome Howl: one refractive shock wave from the wolf's jaw and a much weaker echo, with only a faint violet rim. */
export function howlWave(): SkillEffect {
  const group = new THREE.Group(); group.name = 'fearsome-howl-wave';
  const waves = [[0, 1], [.28, .3]].map(([delay, power]) => {
    const shell = distortionShell('fearsome-howl-distortion'); shell.mesh.name = 'howl-distortion-shell'; group.add(shell.mesh);
    const rim = new THREE.Mesh(new THREE.SphereGeometry(1, 40, 20), effectMaterial(FEAR, 0xf1ebff, 1, .02));
    rim.userData.baseOpacity = .06 * power; rim.name = 'howl-rim'; group.add(rim);
    return { ...shell, rim, delay, power };
  });
  return { group, duration: DURATION * 1000, anchor: 'jaw', update(p, _from, to, time = p * DURATION, ppm = 40) {
    group.position.copy(to);
    for (const { mesh, uniforms, rim, delay, power } of waves) {
      const local = THREE.MathUtils.clamp((p - delay) / (1 - delay), 0, 1);
      const radius = .15 + REACH * (1 - (1 - local) ** 2.4), strength = local > 0 ? Math.min(1, local * 9) * (1 - local) ** 1.6 * power : 0;
      mesh.scale.setScalar(radius); rim.scale.setScalar(radius);
      mesh.visible = rim.visible = strength > .002;
      uniforms.uIor.value = 1 + .2 * strength; uniforms.uThickness.value = 1.2 * strength;
      uniforms.uTime.value = time; uniforms.uRipple.value = strength;
      shadeEffect(rim, time, strength, local, ppm);
    }
  } };
}
